import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { CheckCircle, ChevronDown, ChevronUp } from "lucide-react";
import { changelogData } from "@/config/changelog-data";

interface ChangelogEntry {
  id: string;
  version: string;
  date: string;
  title: string;
  changes: string[];
  relatedModules: string[];
}

export default function ChangelogPage() {
  const [openItems, setOpenItems] = useState<number[]>([0]);

  // Always use static changelog-data.ts as the single source of truth
  const changelogEntries: ChangelogEntry[] = changelogData.map((e, idx) => ({
    id: String(idx),
    version: e.version,
    date: e.date,
    title: e.title,
    changes: e.changes,
    relatedModules:
      ((e as unknown as Record<string, unknown>).related_modules as string[]) ??
      [],
  }));

  const toggleItem = (index: number) => {
    setOpenItems((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  return (
    <div className="p-4 max-w-6xl mx-auto">
      {/* Timeline */}
      <div className="relative pb-10">
        <div className="absolute left-3 top-2 bottom-0 w-px bg-slate-200" />
        <div className="space-y-6">
          {changelogEntries.map((release, idx) => {
            const isOpen = openItems.includes(idx);
            const isLatest = idx === 0;

            return (
              <div key={release.version} className="relative pl-10">
                <div
                  className={`absolute left-0 top-1.5 w-6 h-6 rounded-full flex items-center justify-center border-2 z-10 transition-colors ${
                    isLatest
                      ? "bg-indigo-600 border-indigo-600 text-white"
                      : "bg-white border-slate-300 text-slate-500"
                  }`}
                >
                  {isLatest ? (
                    <span className="text-[10px] font-bold">✓</span>
                  ) : (
                    <span className="text-[10px]">
                      {changelogEntries.length - idx}
                    </span>
                  )}
                </div>

                <Collapsible
                  open={isOpen}
                  onOpenChange={() => toggleItem(idx)}
                  className={`border rounded-lg bg-white transition-all duration-200 ${isLatest ? "border-indigo-200 shadow-sm ring-1 ring-indigo-50" : "border-slate-200 hover:border-slate-300"}`}
                >
                  <CollapsibleTrigger asChild>
                    <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50/50 rounded-t-lg select-none group">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-3">
                          <h3
                            className={`text-sm font-bold ${isLatest ? "text-indigo-700" : "text-slate-700"}`}
                          >
                            {release.version}
                          </h3>
                          {isLatest && (
                            <Badge className="text-[10px] px-1.5 py-0 h-4 bg-indigo-100 text-indigo-700 border-indigo-200 hover:bg-indigo-100 shadow-none">
                              Mới nhất
                            </Badge>
                          )}
                          <span className="text-sm font-medium text-slate-700 group-hover:text-indigo-600 transition-colors">
                            {release.title}
                          </span>
                        </div>
                        <span className="text-xs text-slate-500 font-medium ml-0.5">
                          {new Date(release.date).toLocaleString("vi-VN", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: false,
                          })}
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-slate-400 group-hover:text-slate-600"
                      >
                        {isOpen ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    <div className="px-4 pb-4 pt-0">
                      <div className="h-px bg-slate-100 mb-3" />
                      <ul className="space-y-2.5">
                        {release.changes.map((item: string, i: number) => {
                          const parts = item.split(/(\*\*.*?\*\*)/g);
                          return (
                            <li
                              key={i}
                              className="flex items-start gap-3 text-sm text-slate-600"
                            >
                              <CheckCircle
                                className={`h-4 w-4 mt-0.5 flex-shrink-0 ${isLatest ? "text-indigo-500" : "text-slate-400"}`}
                              />
                              <span className="leading-relaxed">
                                {parts.map((part, idx) => {
                                  if (
                                    part.startsWith("**") &&
                                    part.endsWith("**")
                                  ) {
                                    return (
                                      <strong
                                        key={idx}
                                        className="font-semibold text-slate-800"
                                      >
                                        {part.slice(2, -2)}
                                      </strong>
                                    );
                                  }
                                  return part;
                                })}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                      {release.relatedModules &&
                        release.relatedModules.length > 0 && (
                          <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-2">
                            <span className="text-xs font-semibold text-slate-500">
                              Tài liệu liên quan:
                            </span>
                            <div className="flex gap-2">
                              {release.relatedModules.map((modKey: string) => (
                                <Button
                                  key={modKey}
                                  variant="outline"
                                  size="sm"
                                  className="h-6 text-[10px] px-2 bg-slate-50 hover:bg-white text-indigo-600 border-indigo-100"
                                  asChild
                                >
                                  <a
                                    href={`/guide?module=${modKey}`}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    #{modKey.toUpperCase()}
                                  </a>
                                </Button>
                              ))}
                            </div>
                          </div>
                        )}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
